import { useEffect, useState } from 'react';
import { YStack, type YStackProps } from 'tamagui';

import { cardStyle } from '@/components/card';
import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion';
import { duration } from '@/theme/tokens.generated';

// Issue #150 (component only -- NOT wired into any real loading state
// in this task, that's #147's explicit job per this plan's own scope
// note). Uses `cardStyle` (src/components/card.tsx) as its silhouette
// -- the same rounded/shadowed card shape this app already renders
// real content in (thread cards, match cards), so a skeleton reads as
// "this exact thing, loading" rather than a generic gray box that
// doesn't match what's about to appear.
//
// Pulse via a plain interval flipping a boolean plus `transition="medium"`
// on `opacity` (the #146/#195 motion driver -- Reanimated on native, the
// real CSS driver on web, both wired platform-correctly per
// tamagui.config.ts/tamagui.config.web.ts) rather than a driver-level
// looping/repeat API: neither `@tamagui/animations-reanimated` nor
// `@tamagui/animations-css`'s preset shape exposes an iteration-count or
// repeat option on a *named preset* (checked both packages' createAnimations
// signatures before choosing this) -- looping is normally the CALLER's
// job (Reanimated's own `withRepeat`, or a CSS `animation-iteration-count`
// on a raw keyframe), neither of which composes with Tamagui's `transition`
// preset-name prop this app's other motion already uses. Toggling opacity
// on an interval and letting the SAME `medium` transition preset every
// other component in this app already uses handle the actual animating
// is simpler, platform-uniform, and doesn't require a second animation
// mechanism just for this one component.
// `duration.slow` (tokens/motion.json), not a magic number -- this
// project's own motion rule (brand-and-operations.md: "Duration:
// 100-300ms for UI transitions. Never > 500ms") caps every timing value
// at 500ms; an earlier version of this file used a literal 900ms,
// which broke that ceiling. `slow` is the token's own top step, giving
// the slowest "breathing" cadence the token scale allows -- fast enough
// to read as active, slow enough not to feel frantic on a screen full
// of them.
const PULSE_INTERVAL_MS = duration.slow;

export function Skeleton(props: YStackProps) {
  const [dim, setDim] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    // Issue #147 AC4 -- this interval is the one piece of motion in the
    // app with zero built-in reduced-motion awareness (unlike the
    // Reanimated press-spring driver, whose own `withTiming`/`withSpring`
    // already read the OS setting for free -- see
    // use-reduced-motion.ts's own comment). A skeleton pulsing forever
    // is exactly the kind of persistent, non-essential motion
    // prefers-reduced-motion exists to suppress; skip starting the
    // interval at all rather than starting it and fighting it.
    if (reducedMotion) return;
    const id = setInterval(() => setDim((prev) => !prev), PULSE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [reducedMotion]);

  return (
    <YStack
      {...cardStyle}
      opacity={reducedMotion ? 1 : dim ? 0.5 : 1}
      transition="medium"
      // Bakes the "this is loading" announcement into the primitive
      // now so #147's real wiring (threads/thread-detail/preview list
      // loading states, its own AC #5) doesn't have to retrofit it onto
      // every call site later.
      //
      // `role`/`aria-label`, NOT `accessibilityRole`/`accessibilityLabel`
      // -- confirmed by an actual render (this codebase's own protocol:
      // never state a a11y claim you didn't render and check) that
      // Tamagui's `YStack` does NOT translate the RN-specific
      // accessibility* props into real DOM/ARIA attributes on web the
      // way a raw react-native `View`/`Pressable` does (they arrived as
      // literal, unrecognized `accessibilityrole`/`accessibilitylabel`
      // DOM attributes -- a real React console warning, not a false
      // negative). RN 0.86 (this app's version) natively supports the
      // newer web-ARIA-style props directly -- `role`/`aria-label` --
      // translating them to real native platform accessibility
      // internally, while Tamagui forwards them as-is on web, where
      // they land as real, valid `role`/`aria-label` DOM attributes.
      // One prop pair, both platforms actually correct.
      role="progressbar"
      aria-label="Loading"
      {...props}
    />
  );
}
